import type {
  BillDetail,
  BillVoteDetailResponse,
  CommitteeDetail,
  CommitteeListResponse,
  ClotureExamplesResponse,
  CongressExplainerExamplesResponse,
  ReintroducedBillExamplesResponse,
  ReportedCommitteeExamplesResponse,
  ContactMessageResponse,
  CreateContactMessageRequest,
  DashboardResponse,
  InsightsWeeklySnapshotResponse,
  FeedDegradationMeta,
  FloorScheduleResponse,
  FecCommitteeSearchResponse,
  HouseRollCallVoteDetailResponse,
  LatestActivityItem,
  MeetingDetail,
  MemberDetail,
  MemberListResponse,
  MemberFinanceDetailResponse,
  MemberVoteRecordFilter,
  MemberVoteRecordResponse,
  PagedResult,
  BillFacetsResponse,
  BillListItem,
  AiLegislationInsightResponse,
  MoneyInfluenceInsightResponse,
  SenateFloorVoteDetailResponse,
  MemberListItem,
  MeetingListItem,
  SupportedCongressesResponse,
} from "../../shared/types";

function normalizeFeedDegradationMeta(
  meta: FeedDegradationMeta | null | undefined,
): FeedDegradationMeta {
  return {
    lastSucceededAt:
      typeof meta?.lastSucceededAt === "string" ? meta.lastSucceededAt : null,
    lastError: typeof meta?.lastError === "string" ? meta.lastError : null,
    isDegraded: meta?.isDegraded === true,
  };
}

function normalizeFloorMeta(
  response: Pick<DashboardResponse | FloorScheduleResponse, "floorMeta">,
) {
  return {
    house: normalizeFeedDegradationMeta(response.floorMeta?.house),
    senate: normalizeFeedDegradationMeta(response.floorMeta?.senate),
  };
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");

  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(payload?.error ?? `Request failed: ${response.status}`);
  }

  return (await response.json()) as T;
}

function normalizeDashboardResponse(
  response: DashboardResponse,
): DashboardResponse {
  return {
    ...response,
    floorMeta: normalizeFloorMeta(response),
    recentSenateFloorVotesCount: Number.isFinite(
      response.recentSenateFloorVotesCount,
    )
      ? response.recentSenateFloorVotesCount
      : 0,
    recentSenateFloorVotes: Array.isArray(response.recentSenateFloorVotes)
      ? response.recentSenateFloorVotes
      : [],
    upcomingFloorItemsCount: Number.isFinite(response.upcomingFloorItemsCount)
      ? response.upcomingFloorItemsCount
      : 0,
    upcomingFloorItems: Array.isArray(response.upcomingFloorItems)
      ? response.upcomingFloorItems
      : [],
  };
}

function normalizeFloorScheduleResponse(
  response: FloorScheduleResponse,
): FloorScheduleResponse {
  const houseAgendaCount = Number.isFinite(response.houseAgendaCount)
    ? response.houseAgendaCount
    : 0;
  const senateRecentVotesCount = Number.isFinite(
    response.senateRecentVotesCount,
  )
    ? response.senateRecentVotesCount
    : 0;

  return {
    ...response,
    floorMeta: normalizeFloorMeta(response),
    houseAgendaCount,
    houseAgendaPage: Number.isFinite(response.houseAgendaPage)
      ? Math.max(1, response.houseAgendaPage)
      : 1,
    houseAgendaPageSize: Number.isFinite(response.houseAgendaPageSize)
      ? Math.max(1, response.houseAgendaPageSize)
      : 5,
    houseAgendaTotalPages: Number.isFinite(response.houseAgendaTotalPages)
      ? Math.max(1, response.houseAgendaTotalPages)
      : Math.max(1, Math.ceil(houseAgendaCount / 5)),
    houseAgendaItems: Array.isArray(response.houseAgendaItems)
      ? response.houseAgendaItems
      : [],
    senateRecentVotesCount,
    senateRecentVotesPage: Number.isFinite(response.senateRecentVotesPage)
      ? Math.max(1, response.senateRecentVotesPage)
      : 1,
    senateRecentVotesPageSize: Number.isFinite(
      response.senateRecentVotesPageSize,
    )
      ? Math.max(1, response.senateRecentVotesPageSize)
      : 5,
    senateRecentVotesTotalPages: Number.isFinite(
      response.senateRecentVotesTotalPages,
    )
      ? Math.max(1, response.senateRecentVotesTotalPages)
      : Math.max(1, Math.ceil(senateRecentVotesCount / 5)),
    senateRecentVotes: Array.isArray(response.senateRecentVotes)
      ? response.senateRecentVotes
      : [],
  };
}

export const api = {
  meta: () => apiFetch<SupportedCongressesResponse>("/api/v1/meta/congresses"),
  dashboard: async (params: URLSearchParams) =>
    normalizeDashboardResponse(
      await apiFetch<DashboardResponse>(
        `/api/v1/dashboard?${params.toString()}`,
      ),
    ),
  latestActivity: (params: URLSearchParams) =>
    apiFetch<PagedResult<LatestActivityItem>>(
      `/api/v1/activity/latest?${params.toString()}`,
    ),
  contact: (payload: CreateContactMessageRequest) =>
    apiFetch<ContactMessageResponse>("/api/v1/contact", {
      body: JSON.stringify(payload),
      method: "POST",
    }),
  committees: (params: URLSearchParams) =>
    apiFetch<CommitteeListResponse>(`/api/v1/committees?${params.toString()}`),
  fecCommittees: (params: URLSearchParams) =>
    apiFetch<FecCommitteeSearchResponse>(
      `/api/v1/fec/committees?${params.toString()}`,
    ),
  committeeDetail: (congress: string, chamber: string, committeeCode: string) =>
    apiFetch<CommitteeDetail>(
      `/api/v1/committees/${congress}/${chamber}/${encodeURIComponent(committeeCode)}`,
    ),
  scheduleFloor: async (params: URLSearchParams) =>
    normalizeFloorScheduleResponse(
      await apiFetch<FloorScheduleResponse>(
        `/api/v1/schedule/floor?${params.toString()}`,
      ),
    ),
  bills: (params: URLSearchParams) =>
    apiFetch<PagedResult<BillListItem>>(`/api/v1/bills?${params.toString()}`),
  billsFacets: (params: URLSearchParams) =>
    apiFetch<BillFacetsResponse>(`/api/v1/bills/facets?${params.toString()}`),
  aiLegislationInsight: () =>
    apiFetch<AiLegislationInsightResponse>("/api/v1/insights/ai-legislation"),
  insightsWeeklySnapshot: () =>
    apiFetch<InsightsWeeklySnapshotResponse>(
      "/api/v1/insights/weekly-snapshot",
    ),
  moneyInfluenceInsight: () =>
    apiFetch<MoneyInfluenceInsightResponse>("/api/v1/insights/money-influence"),
  clotureExamples: () =>
    apiFetch<ClotureExamplesResponse>("/api/v1/insights/cloture/examples"),
  congressExplainerExamples: (slug: string) =>
    apiFetch<CongressExplainerExamplesResponse>(
      `/api/v1/insights/congress-explained/${encodeURIComponent(slug)}/examples`,
    ),
  reintroducedBillExamples: () =>
    apiFetch<ReintroducedBillExamplesResponse>(
      "/api/v1/insights/why-bills-get-reintroduced/examples",
    ),
  reportedCommitteeExamples: () =>
    apiFetch<ReportedCommitteeExamplesResponse>(
      "/api/v1/insights/reported-by-committee/examples",
    ),
  billDetail: (congress: string, billType: string, billNumber: string) =>
    apiFetch<BillDetail>(`/api/v1/bills/${congress}/${billType}/${billNumber}`),
  billVoteDetail: (
    congress: string,
    billType: string,
    billNumber: string,
    voteId: string,
  ) =>
    apiFetch<BillVoteDetailResponse>(
      `/api/v1/bills/${congress}/${billType}/${billNumber}/votes/${encodeURIComponent(voteId)}`,
    ),
  houseRollCallVoteDetail: (
    congress: string,
    sessionNumber: string,
    rollNumber: string,
  ) =>
    apiFetch<HouseRollCallVoteDetailResponse>(
      `/api/v1/votes/house/${congress}/${sessionNumber}/${rollNumber}`,
    ),
  senateFloorVoteDetail: (
    congress: string,
    sessionNumber: string,
    voteNumber: string,
  ) =>
    apiFetch<SenateFloorVoteDetailResponse>(
      `/api/v1/votes/senate/${congress}/${sessionNumber}/${voteNumber}`,
    ),
  members: (params: URLSearchParams) =>
    apiFetch<MemberListResponse>(`/api/v1/members?${params.toString()}`),
  memberDetail: (bioguideId: string, congress?: string) =>
    apiFetch<MemberDetail>(
      `/api/v1/members/${bioguideId}${congress ? `?congress=${congress}` : ""}`,
    ),
  memberFinanceDetail: (bioguideId: string, congress?: string) =>
    apiFetch<MemberFinanceDetailResponse>(
      `/api/v1/members/${bioguideId}/finance${congress ? `?congress=${congress}` : ""}`,
    ),
  memberVotes: (
    bioguideId: string,
    page = 1,
    filter: MemberVoteRecordFilter = "all",
  ) =>
    apiFetch<MemberVoteRecordResponse>(
      `/api/v1/members/${bioguideId}/votes?page=${page}&filter=${encodeURIComponent(filter)}`,
    ),
  meetings: (params: URLSearchParams) =>
    apiFetch<PagedResult<MeetingListItem>>(
      `/api/v1/meetings?${params.toString()}`,
    ),
  meetingDetail: (congress: string, chamber: string, eventId: string) =>
    apiFetch<MeetingDetail>(
      `/api/v1/meetings/${congress}/${chamber}/${eventId}`,
    ),
};
